'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      setIsMobileMenuOpen(false)
    }
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group">
          <div className="flex items-center gap-2">
            <Image
              src="/assets/images/4-1779457047236.png"
              alt="FrenaCal logo"
              width={40}
              height={40}
              className="h-10 w-10 object-contain"
              priority
            />
            <div className="flex flex-col items-start">
              <span className="text-xl font-bold text-primary leading-tight">FrenaCal</span>
              <span className="text-[9px] font-semibold tracking-widest text-muted-foreground uppercase leading-tight">
                Green · Proven · Powerful
              </span>
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <button
            onClick={() => scrollToSection('hoe-werkt-het')}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Hoe werkt het
          </button>
          <button
            onClick={() => scrollToSection('producten')}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Producten
          </button>
          <button
            onClick={() => scrollToSection('vergelijking')}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Vergelijking
          </button>
          <button
            onClick={() => scrollToSection('reviews')}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Reviews
          </button>
          <button
            onClick={() => scrollToSection('geschikt-voor')}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Geschikt voor
          </button>
          <Link
            href="/partners"
            className="text-sm font-medium text-[#33BEBE] hover:text-[#1B3A6B] transition-colors"
          >
            Partners
          </Link>
        </div>

        {/* CTA Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => scrollToSection('contact-form')}
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 btn-transition"
          >
            Gratis advies
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-border">
          <div className="px-6 py-4 space-y-3">
            <button
              onClick={() => scrollToSection('hoe-werkt-het')}
              className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Hoe werkt het
            </button>
            <button
              onClick={() => scrollToSection('producten')}
              className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Producten
            </button>
            <button
              onClick={() => scrollToSection('vergelijking')}
              className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Vergelijking
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('geschikt-voor')}
              className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Geschikt voor
            </button>
            <Link
              href="/partners"
              className="block w-full text-left py-2 text-sm font-medium text-[#33BEBE] hover:text-[#1B3A6B] transition-colors"
            >
              Partners
            </Link>
            <button
              onClick={() => scrollToSection('contact-form')}
              className="w-full mt-4 inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground"
            >
              Gratis advies
            </button>
          </div>
        </div>
      )}
    </header>
  )
}